import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Banknote, Clock, CheckCircle2, BarChart3, Globe } from 'lucide-react';
import { apiCall } from './api';

const fmtUsd = n => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} USD`;
const fmtCdf = n => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} CDF`;
const fmtCur = (n, c) => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${c}`;

export default function AdminRevenue({ token }) {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [byCountry, setByCountry] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, p, bc] = await Promise.all([
        apiCall('/admin/revenue-stats', 'GET', null, token),
        apiCall('/admin/payments', 'GET', null, token),
        apiCall('/admin/revenue-by-country', 'GET', null, token),
      ]);
      setStats(s);
      setPayments(p);
      setByCountry(bc);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { reload(); }, [token]);

  // Build "revenue per month" from validated payments (last 12 months)
  const monthly = useMemo(() => {
    const map = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[k] = { key: k, label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }), usd: 0, cdf: 0, count: 0 };
    }
    payments.filter(p => p.status === 'validated').forEach(p => {
      const d = new Date(p.validated_at || p.submitted_at);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (map[k]) {
        if ((p.currency || '').toUpperCase() === 'CDF') map[k].cdf += Number(p.amount || 0);
        else map[k].usd += Number(p.amount || 0);
        map[k].count += 1;
      }
    });
    return Object.values(map);
  }, [payments]);

  const maxBar = Math.max(1, ...monthly.map(m => m.usd + m.cdf / 2500)); // CDF approx normalized

  const filtered = useMemo(() => {
    if (filter === 'all') return payments;
    return payments.filter(p => p.status === filter);
  }, [payments, filter]);

  const conversion = useMemo(() => {
    const validated = payments.filter(p => p.status === 'validated').length;
    const total = payments.length;
    if (!total) return 0;
    return Math.round((validated / total) * 100);
  }, [payments]);

  if (loading) {
    return <div className="text-center py-10 text-white/50">Chargement des revenus...</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-revenue-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={DollarSign} label="Revenus USD" value={fmtUsd(stats?.by_currency?.USD)} accent="emerald" testid="kpi-usd" />
        <Kpi icon={Banknote} label="Revenus CDF" value={fmtCdf(stats?.by_currency?.CDF)} accent="yellow" testid="kpi-cdf" />
        <Kpi icon={CheckCircle2} label="Validés" value={stats?.validated_count ?? 0} accent="green" testid="kpi-validated" />
        <Kpi icon={Clock} label="En attente" value={stats?.pending_count ?? 0} accent="amber" testid="kpi-pending" />
      </div>

      {/* Conversion bar */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2 text-sm">
          <div className="flex items-center gap-2 text-white/70"><TrendingUp size={14} className="text-emerald-300" />Taux de conversion (validés / total)</div>
          <div className="font-bold text-emerald-300" data-testid="conversion-rate">{conversion}%</div>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-yellow-400 transition-all" style={{ width: `${conversion}%` }} />
        </div>
      </div>

      {/* Monthly bar chart */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <div className="flex items-center gap-2 text-sm font-bold text-emerald-300 mb-4"><BarChart3 size={14} />Revenus mensuels (12 mois)</div>
        <div className="flex items-end gap-1.5 h-40" data-testid="monthly-chart">
          {monthly.map(m => {
            const norm = m.usd + m.cdf / 2500;
            const h = norm ? Math.max(4, (norm / maxBar) * 100) : 2;
            return (
              <div key={m.key} className="flex-1 flex flex-col items-center group" title={`${m.label} · ${fmtUsd(m.usd)} · ${fmtCdf(m.cdf)} · ${m.count} ventes`}>
                <div className="w-full flex flex-col justify-end h-32">
                  <div className="w-full bg-gradient-to-t from-emerald-500 to-yellow-400 rounded-t group-hover:opacity-80" style={{ height: `${h}%` }} />
                </div>
                <div className="text-[9px] text-white/40 mt-1 rotate-45 origin-left whitespace-nowrap">{m.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top modules */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <div className="text-sm font-bold text-emerald-300 mb-3">Top modules vendus</div>
        <div className="space-y-2">
          {Object.entries(stats?.by_module || {}).length === 0 && (
            <div className="text-xs text-white/40">Aucun revenu encore.</div>
          )}
          {Object.entries(stats?.by_module || {})
            .sort((a, b) => (b[1].USD + b[1].CDF / 2500) - (a[1].USD + a[1].CDF / 2500))
            .slice(0, 5)
            .map(([mid, v]) => (
              <div key={mid} className="flex items-center justify-between bg-slate-900/40 rounded-lg p-2 text-xs">
                <div className="font-mono text-white/80">{mid}</div>
                <div className="flex gap-3 text-right">
                  <span className="text-emerald-300">{fmtUsd(v.USD)}</span>
                  {v.CDF > 0 && <span className="text-yellow-300">{fmtCdf(v.CDF)}</span>}
                  <span className="text-white/50">{v.count} vente{v.count > 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Revenue by country & currency */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid="revenue-by-country">
        <div className="flex items-center gap-2 text-sm font-bold text-emerald-300 mb-3"><Globe size={14} />Revenus par pays & devise</div>
        {byCountry?.by_currency && Object.keys(byCountry.by_currency).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(byCountry.by_currency).sort((a, b) => b[1] - a[1]).map(([cur, tot]) => (
              <div key={cur} className="bg-slate-900/60 border border-white/10 rounded-xl px-3 py-2" data-testid={`rev-currency-${cur}`}>
                <div className="text-[10px] uppercase tracking-wider text-white/50">{cur}</div>
                <div className="font-black text-emerald-300">{fmtCur(tot, cur)}</div>
              </div>
            ))}
          </div>
        )}
        {(!byCountry?.countries || byCountry.countries.length === 0) ? (
          <div className="text-xs text-white/40">Aucun revenu encore.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-white/50 text-left"><tr><th className="py-2">Pays</th><th>Montants (par devise)</th><th className="text-right">Ventes</th></tr></thead>
              <tbody>
                {byCountry.countries.map(row => (
                  <tr key={row.country_code || 'none'} className="border-t border-white/5" data-testid={`rev-country-${row.country_code || 'none'}`}>
                    <td className="py-2 font-semibold text-white/80">{row.name}</td>
                    <td className="text-emerald-300">
                      {Object.entries(row.currencies).map(([cur, tot]) => (
                        <span key={cur} className="inline-block mr-3">{fmtCur(tot, cur)}</span>
                      ))}
                    </td>
                    <td className="text-right text-white/60">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent payments */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-emerald-300">Derniers paiements</div>
          <div className="flex gap-1 text-xs">
            {['all', 'pending', 'validated', 'rejected'].map(f => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`px-2 py-1 rounded-lg ${filter === f ? 'bg-emerald-500 text-slate-950 font-bold' : 'bg-white/5 text-white/60 hover:text-white'}`}>
                {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : f === 'validated' ? 'Validés' : 'Rejetés'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-white/50 text-left">
              <tr>
                <th className="py-2">Date</th><th>Étudiant</th><th>Module</th><th>Méthode</th><th>Montant</th><th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 30).map(p => (
                <tr key={p.id} className="border-t border-white/5" data-testid={`pay-row-${p.id}`}>
                  <td className="py-2 text-white/60">{new Date(p.submitted_at).toLocaleDateString('fr-FR')}</td>
                  <td className="text-white/80">{p.user_email}</td>
                  <td className="font-mono text-white/60">{p.module_id}</td>
                  <td className="text-white/60">{p.method}</td>
                  <td className="font-bold text-emerald-300">{p.currency === 'CDF' ? fmtCdf(p.amount) : fmtUsd(p.amount)}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      p.status === 'validated' ? 'bg-emerald-500/20 text-emerald-300' :
                      p.status === 'rejected'  ? 'bg-red-500/20 text-red-300' :
                                                 'bg-amber-500/20 text-amber-300'
                    }`}>{p.status}</span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-white/40">Aucun paiement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, testid }) {
  const cls = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-400/30 text-emerald-300',
    yellow:  'from-yellow-500/20  to-yellow-500/5  border-yellow-400/30  text-yellow-300',
    green:   'from-green-500/20   to-green-500/5   border-green-400/30   text-green-300',
    amber:   'from-amber-500/20   to-amber-500/5   border-amber-400/30   text-amber-300',
  }[accent] || '';
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${cls} border rounded-2xl p-3`} data-testid={testid}>
      <Icon size={16} className="mb-1.5" />
      <div className="text-lg font-black truncate">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{label}</div>
    </motion.div>
  );
}
